import { noop } from 'lodash';
import type { FC, PropsWithChildren } from 'react';
import React, { useCallback, useState } from 'react';

import { AppLayoutBreakpoint, useContainerWidth } from '../../../../util/responsiveness';
import useElementSize from '../../../../util/useElementSize';
import Flex, { FlexItem } from '../../../Flex';
import ContentContainer from '../../ContentContainer';
import { MainAreaRowContextProvider } from '../../MainAreaRowContextProvider';
import { useAppLayoutContext, useContentContainerContext } from '../../hooks';

import FooterRow from './FooterRow';
import LeftNavContainer from './LeftNavContainer';
import LoadableBackButtonRow from './LoadableBackButtonRow';
import { useClassNames } from './MainAreaRow.styles';
import type { MainAreaRowProps } from './MainAreaRow.types';
import MainAreaRowContent from './MainAreaRowContent';
import type { TemplateProps } from './Template.types';

const getPaddingLeft = (containerWidth: number, hasBackButton: boolean, leftNav?: TemplateProps['leftNav']) => {
  if (containerWidth <= AppLayoutBreakpoint.Width568) {
    return 0;
  }
  const isBackButtonFixed = containerWidth <= AppLayoutBreakpoint.Width1185;

  return hasBackButton && (isBackButtonFixed || leftNav) ? 11 : 0;
};

const MainAreaRow: FC<PropsWithChildren<MainAreaRowProps>> = ({
  hasHeroBackground,
  children,
  footer,
  leftNav,
  backButtonAriaLabel = '',
  onBackButtonClick = noop,
  scrollPaddingTop,
  scrollPaddingBottom,
}) => {
  const { shouldDisableScroll, shouldFillAvailableSpace } = useContentContainerContext();
  const { hasBackButton } = useAppLayoutContext()!;
  const containerWidth = useContainerWidth();
  const classNames = useClassNames();

  const shouldShowLeftNav = leftNav && !shouldFillAvailableSpace;
  const shouldShowBackButton = hasBackButton && !shouldFillAvailableSpace;

  const footerRow = footer ? <FooterRow>{footer}</FooterRow> : null;

  const [mainAreaElement, setMainAreaElement] = useState<HTMLElement | null>(null);
  const mainAreaCallbackRef = useCallback((node: HTMLDivElement | null) => {
    setMainAreaElement(node);
  }, []);
  const { height: mainAreaHeight } = useElementSize(mainAreaElement);

  const leftNavigation = shouldShowLeftNav ? (
    <LeftNavContainer contentHeight={mainAreaHeight}>{leftNav}</LeftNavContainer>
  ) : null;

  const backButtonRow = shouldShowBackButton ? (
    <LoadableBackButtonRow hasLeftNav={!!leftNav} ariaLabel={backButtonAriaLabel} onClick={onBackButtonClick} />
  ) : null;

  return (
    <MainAreaRowContextProvider height={mainAreaHeight}>
      <Flex grow={1} height="100%" maxHeight="100%">
        {leftNavigation}
        <Flex direction="column" grow={1} ref={mainAreaCallbackRef}>
          {backButtonRow}
          <FlexItem className={classNames.container} basis="100%">
            <ContentContainer
              disableScrolling={shouldDisableScroll}
              forceScrollable={shouldFillAvailableSpace}
              paddingLeft={getPaddingLeft(containerWidth, hasBackButton, leftNav)}
              scrollPaddingTop={scrollPaddingTop}
              scrollPaddingBottom={scrollPaddingBottom}
            >
              <MainAreaRowContent hasHeroBackground={!!hasHeroBackground}>{children}</MainAreaRowContent>
            </ContentContainer>
          </FlexItem>
          {footerRow}
        </Flex>
      </Flex>
    </MainAreaRowContextProvider>
  );
};
export default MainAreaRow;
